import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getAgents } from '../services/api';

const AGENT_COLORS = {
  'main-oi': 'rgba(147, 197, 253, 0.3)',      // Blue
  'wellness-oi': 'rgba(167, 243, 208, 0.3)',  // Green
  'education-oi': 'rgba(253, 230, 138, 0.3)', // Yellow
  'eldercare-oi': 'rgba(216, 180, 254, 0.3)', // Purple
  'parenting-oi': 'rgba(252, 165, 165, 0.3)', // Red/Pink
  'home-oi': 'rgba(254, 215, 170, 0.3)',      // Orange
  'kids-oi': 'rgba(165, 243, 252, 0.3)',      // Cyan
};

function AgentTile({ agent, isSelected, onClick, delay }) {
  const bgColor = AGENT_COLORS[agent.agentId] || 'rgba(255,255,255,0.15)';
  
  return (
    <motion.button
      onClick={onClick}
      initial={{ opacity: 0, y: 30, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ 
        delay: delay * 0.1, 
        type: 'spring', 
        stiffness: 200, 
        damping: 20 
      }}
      whileHover={{ scale: 1.05, y: -4 }}
      whileTap={{ scale: 0.98 }}
      style={{
        width: '100%',
        maxWidth: 160,
        aspectRatio: '1',
        padding: 16,
        borderRadius: 24,
        border: isSelected 
          ? '2px solid rgba(255,255,255,0.6)' 
          : '1px solid rgba(255,255,255,0.2)',
        background: bgColor,
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        boxShadow: isSelected 
          ? '0 8px 32px rgba(0,0,0,0.3), inset 0 0 0 1px rgba(255,255,255,0.2)'
          : '0 4px 16px rgba(0,0,0,0.2)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        textAlign: 'center',
        transition: 'border 0.2s, box-shadow 0.2s',
      }}
    >
      <span style={{ fontSize: 40 }}>{agent.templateIcon || '🤖'}</span>
      <span style={{ 
        fontSize: 15, 
        fontWeight: 600, 
        color: 'rgba(255,255,255,0.95)',
        lineHeight: 1.2,
      }}>
        {agent.name}
      </span>
      {agent.description && (
        <span style={{ 
          fontSize: 11, 
          color: 'rgba(255,255,255,0.6)',
          lineHeight: 1.3,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {agent.description.split('.')[0]}
        </span>
      )}
    </motion.button>
  );
}

export default function AgentPicker({ onSelect, currentAgentId, showTitle = true }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(currentAgentId || null);

  useEffect(() => {
    loadAgents();
  }, []);

  const loadAgents = async () => {
    try {
      const data = await getAgents();
      // If no family agents, show templates as available agents
      const agentList = data.agents?.length > 0 
        ? data.agents 
        : (data.templates || []).map(t => ({
            id: t.id,
            agentId: t.id,
            name: t.name,
            description: t.description,
            templateIcon: t.icon,
            templateCategory: t.category,
          }));
      setAgents(agentList);
      
      // Auto-select first if none selected
      if (!selected && agentList.length > 0) {
        const defaultAgent = agentList.find(a => a.isDefault || a.agentId === 'main-oi') || agentList[0];
        setSelected(defaultAgent.agentId || defaultAgent.id);
      }
    } catch (err) {
      console.error('Failed to load agents:', err);
      // Fallback to default agent
      setAgents([{
        id: 'main-oi',
        agentId: 'main-oi',
        name: 'Oi',
        description: 'Your family assistant',
        templateIcon: '🏠',
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (agent) => {
    const agentId = agent.agentId || agent.id;
    setSelected(agentId);
    if (onSelect) {
      onSelect(agent);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 200,
        color: 'rgba(255,255,255,0.6)',
      }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          style={{ fontSize: 24 }}
        >
          ⏳
        </motion.div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '28px 20px',
      width: '100%',
      maxWidth: 400,
      margin: '0 auto',
      background: 'rgba(0,0,0,0.45)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderRadius: 28,
      border: '1px solid rgba(255,255,255,0.15)',
    }}>
      {showTitle && (
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            fontSize: 22,
            fontWeight: 600,
            color: 'rgba(255,255,255,0.95)',
            marginBottom: 8,
            textAlign: 'center',
          }}
        >
          Who would you like to talk to?
        </motion.h2>
      )}
      
      {showTitle && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          style={{
            fontSize: 14,
            color: 'rgba(255,255,255,0.6)',
            marginBottom: 24,
            textAlign: 'center',
          }}
        >
          Each agent specializes in different areas
        </motion.p>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 16,
        width: '100%',
        maxWidth: 340,
      }}>
        <AnimatePresence>
          {agents.map((agent, idx) => (
            <AgentTile
              key={agent.agentId || agent.id}
              agent={agent}
              isSelected={selected === (agent.agentId || agent.id)}
              onClick={() => handleSelect(agent)}
              delay={idx}
            />
          ))}
        </AnimatePresence>
      </div>

      {selected && (
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          onClick={() => onSelect && onSelect(agents.find(a => (a.agentId || a.id) === selected))}
          style={{
            marginTop: 24,
            padding: '14px 48px',
            borderRadius: 28,
            border: 'none',
            background: 'rgba(255,255,255,0.2)',
            backdropFilter: 'blur(12px)',
            color: 'rgba(255,255,255,0.95)',
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          }}
        >
          Start Chat →
        </motion.button>
      )}
    </div>
  );
}
